import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const CTASection = () => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const bgRef = useRef(null);
  const primaryBtnRef = useRef(null);
  const secondaryBtnRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    // Animate background gradient
    gsap.to(bgRef.current, {
      backgroundPosition: "200% center",
      duration: 20,
      repeat: -1,
      yoyo: true,
      ease: "none",
    });

    // Animate section into view
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    tl.from(containerRef.current, {
      opacity: 0,
      scale: 0.9,
      duration: 1,
      ease: "power3.out",
    }).from(
      [primaryBtnRef.current, secondaryBtnRef.current],
      {
        opacity: 0,
        scale: 0.8,
        duration: 0.5,
        stagger: 0.2,
        ease: "back.out(1.7)",
        onComplete: () => {
          // Pulse buttons once
          gsap.to([primaryBtnRef.current, secondaryBtnRef.current], {
            scale: 1.05,
            duration: 0.3,
            yoyo: true,
            repeat: 1,
            ease: "power1.inOut",
          });
        },
      },
      "-=0.5"
    );

    return () => {
      tl.kill();
      gsap.killTweensOf(bgRef.current);
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div
          ref={containerRef}
          className="shadow-black/20 relative overflow-hidden rounded-3xl shadow-xl"
        >
          <div
            ref={bgRef}
            className="via-emerald-800 absolute inset-0 bg-gradient-to-r from-green-900 to-amber-800"
            style={{ backgroundSize: "200% 200%" }}
          ></div>
          <div className="relative px-6 py-16 text-center">
            <h2 className="font-serif text-3xl font-bold text-white md:text-5xl">
              Join the Heartbeat of Our Community
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">
              Whether near or far, your contribution matters. Become a member,
              share your ideas, or support our projects.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                ref={primaryBtnRef}
                to="/auth/sign-up"
                className="inline-block transform rounded-full bg-amber-500 px-8 py-4 font-bold text-green-900 transition-transform duration-300 hover:scale-105 hover:shadow-lg"
              >
                Become a Member
              </Link>
              <Link
                ref={secondaryBtnRef}
                to="/projects"
                className="bg-transparent inline-block transform rounded-full border-2 border-white px-8 py-4 font-bold text-white transition-transform duration-300 hover:scale-105 hover:bg-white/10"
              >
                Explore Projects
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
